/**
 * TodaysSchedule.jsx
 * src/pages/assistant/TodaysSchedule.jsx
 */

import { useState, useEffect } from "react";
import { BSModal, DeleteModal, Avatar, PageHeader, Loading, EmptyState, useConfirm, ConfirmDialog, apiFetch } from "../../components/helpers";
import { useNavigate } from "react-router-dom";

const STATUSES = ["Pending", "Waiting", "In Progress", "Completed", "Cancelled", "Blocked"];
const STATUS_BADGE = {
  Pending:      "text-bg-secondary",
  Waiting:      "text-bg-info",
  "In Progress":"text-bg-warning",
  Completed:    "text-bg-success",
  Cancelled:    "text-bg-danger",
  Blocked:      "text-bg-dark"
};
const EMPTY_FORM = { 
  schedule_id: null, 
  patient_name: "", 
  doctor_id: "", 
  appointment_date: new Date().toISOString().split('T')[0],
  appointment_time: "", 
  treatment: "", 
  notes: "", 
  status: "Pending" 
};

export default function TodaysSchedule() {
  const navigate                    = useNavigate();
  const [items,      setItems]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [filter,     setFilter]     = useState("All");
  const [modalOpen,  setModalOpen]  = useState(false);
  const [deleteId,   setDeleteId]   = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [doctors,    setDoctors]    = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState("");
  const [flash,      setFlash]      = useState("");
  const { confirm, confirmProps }   = useConfirm();

  // Calendar feature properties
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [patientModal, setPatientModal] = useState(null);

  const displayDate = new Date(selectedDate).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const load = async () => {
    setLoading(true);
    try {
      const resp = await apiFetch(`get_todays_appointments.php?date=${selectedDate}`);
      if (resp.status) setItems(resp.data || []);
      
      const docs = await apiFetch("get_all_doctors.php");
      if (docs.status) setDoctors(docs.data || []);

    } catch (e) { 
      console.error("Load failed", e);
      setItems([]); 
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [selectedDate]);

  const showFlash = (msg) => { setFlash(msg); setTimeout(() => setFlash(""), 3000); };

  const openAdd  = () => { setForm({ ...EMPTY_FORM, appointment_date: selectedDate }); setError(""); setModalOpen(true); };
  const openBlock = () => { setForm({ ...EMPTY_FORM, appointment_date: selectedDate, patient_name: "Blocked", status: "Blocked" }); setError(""); setModalOpen(true); };
  const openEdit = (item) => { setForm({ ...item }); setError(""); setModalOpen(true); };

  const handleCheckin = async (id) => {
    const ok = await confirm({ title: "Check-in Patient", message: "Mark this patient as checked in (Waiting)?", confirmText: "Yes, Check In", variant: "primary", icon: "✅" });
    if (!ok) return;
    try {
      const json = await apiFetch("update_appointment_status.php", { method: "POST", body: { appointment_id: id, status: "Waiting" } });
      if (json.status) { showFlash("Patient checked in!"); load(); }
    } catch {}
  };

  const handleStatusChange = async (id, status, appointment) => {
    try {
      const json = await apiFetch("update_appointment_status.php", { method: "POST", body: { appointment_id: id, status } });
      if (json.status) { 
        showFlash("Status updated!"); 
        load(); 
        
        if (status === "Completed") {
           const doBill = await confirm({ 
             title: "Generate Bill?", 
             message: "Would you like to generate a bill for this completed treatment?", 
             confirmText: "Generate Bill", 
             icon: "💳" 
           });
           if (doBill) {
               // ✅ Create the bill in DB first, then navigate
               try {
                 await apiFetch("create_bill.php", {
                   method: "POST",
                   body: {
                     patient_id: appointment.patient_id,
                     doctor_id: appointment.doctor_id,
                     treatment: appointment.treatment || appointment.notes || "Dental Treatment",
                     bill_date: appointment.appointment_date || new Date().toISOString().split("T")[0],
                     total_amount: 0,   // Assistant can fill in amount on billing page
                     paid_amount: 0,
                     discount: 0,
                     payment_method: "Cash",
                     payment_status: "Unpaid",
                     notes: `Auto-generated from appointment on ${appointment.appointment_date}`
                   }
                 });
               } catch (e) {
                 console.error("Bill creation failed:", e);
               }
               navigate(`/assistant/billing`);
           } else {
               const doFollow = await confirm({ title: "Book Follow-up?", message: "Should we book the next visit for this patient?", confirmText: "Book Visit", icon: "🔄" });
               if (doFollow) {
                   setForm({ ...EMPTY_FORM, patient_name: appointment.patient_name, doctor_id: appointment.doctor_id, treatment: appointment.treatment });
                   setModalOpen(true);
               }
           }
        }
      }
    } catch {}
  };

  const handleWhatsApp = (patient) => {
    if (!patient.mobile) return alert("This patient doesn't have a mobile number on record.");
    const msg = encodeURIComponent(`Hello ${patient.patient_name}, this is a gentle reminder regarding your dental appointment at Om Dental Clinic.`);
    window.open(`https://wa.me/${patient.mobile}?text=${msg}`, "_blank");
  };

  const handleSubmit = async () => {
    if (!form.patient_name && form.status !== 'Blocked') return setError("Patient name is required");
    if (!form.doctor_id)    return setError("Doctor is required");
    if (!form.appointment_time)    return setError("Time is required");
    setSubmitting(true); setError("");

    try {
      const isEdit = !!form.schedule_id;
      const endpoint = isEdit ? "update_appointment.php" : "book_appointment.php";
      
      const payload = {
        ...form,
        appointment_id: form.schedule_id,
        patient_name: form.patient_name !== "Blocked" ? form.patient_name : null,
        doctor_id: form.doctor_id,
        appointment_date: form.appointment_date,
        appointment_time: form.appointment_time,
      };

      const json = await apiFetch(endpoint, { method: "POST", body: payload });
      if (json.status) { 
        setModalOpen(false); 
        showFlash(json.message || "Saved!"); 
        load(); 
      } else {
        setError(json.message || "Server returned an error");
      }
    } catch (err) { 
      setError("Network error: " + (err.message || "Unable to reach server")); 
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    try {
      const json = await apiFetch("delete_appointment.php", { method: "POST", body: { appointment_id: deleteId } });
      if (json.status) { setDeleteId(null); showFlash("Appointment removed!"); load(); }
    } catch {}
  };

  const filtered = items.filter(a =>
    (filter === "All" || a.status === filter) &&
    (a.patient_name?.toLowerCase().includes(search.toLowerCase()) ||
     a.doctor_name?.toLowerCase().includes(search.toLowerCase()))
  );

  const inp = "form-control";

  return (
    <div className="p-3 p-lg-4" style={{ minHeight: "100vh", fontFamily: "'Nunito','Segoe UI',sans-serif" }}>
      <ConfirmDialog {...confirmProps} />

      <PageHeader title="Schedule Manager" subtitle={displayDate}>
        {/* Calendar View Date Picker */}
        <input type="date" className={inp} value={selectedDate} onChange={e => setSelectedDate(e.target.value)} style={{ width: 140, fontSize: 13, fontWeight: "bold" }} />

        <select className="form-select" value={filter} onChange={e => setFilter(e.target.value)} style={{ width: 130, fontSize: 13 }}>
          <option value="All">All Status</option>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <input className={inp} placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 170, fontSize: 13 }} />
        
        {/* Time Block Button */}
        <button className="btn fw-bold text-dark px-3" style={{ background: "#e2e6ea", borderRadius: 10, fontSize: 13 }} onClick={openBlock}>⏰ Block Time</button>
        <button className="btn fw-bold text-white px-4" style={{ background: "#D85A30", borderRadius: 10, fontSize: 13 }} onClick={openAdd}>+ Add Call</button>
      </PageHeader>

      {flash && <div className="alert alert-success py-2 px-3 mb-3" style={{ fontSize: 13 }}>{flash}</div>}

      <div className="card border-0 shadow-sm" style={{ borderRadius: 16 }}>
        <div className="card-body p-0">
          {loading ? <Loading /> : filtered.length === 0 ? <EmptyState message={`No appointments found for ${displayDate}.`} /> : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead style={{ background: "#FAFBFC" }}>
                  <tr>
                    {["Time","Patient","Doctor","Status","Actions"].map(h => (
                      <th key={h} className="text-muted fw-bold border-0 px-4 py-3" style={{ fontSize: 10, letterSpacing: ".6px", textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a, i) => (
                    <tr key={i} style={{ background: a.status === 'Blocked' ? '#fcf8f8' : 'transparent' }}>
                      <td className="px-4" style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 600, color: "#0A1628" }}>{a.appointment_time}</td>
                      <td className="px-4">
                        <div className="d-flex align-items-center gap-2">
                          <Avatar name={a.patient_name} size={28} />
                          {a.status === 'Blocked' ? (
                              <span className="fw-semibold text-danger" style={{ fontSize: 13 }}>{a.patient_name} ({a.treatment || 'Unavailable'})</span>
                          ) : (
                              <span className="fw-semibold text-primary" style={{ fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setPatientModal(a)} title="View Summary">{a.patient_name}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 fw-semibold" style={{ fontSize: 13, color: "#D85A30" }}>{a.doctor_name}</td>
                      <td className="px-4">
                        <select value={a.status} onChange={e => handleStatusChange(a.schedule_id, e.target.value, a)}
                          className="form-select form-select-sm border-0 fw-bold"
                          style={{ fontSize: 11, width: "auto", background: "transparent", cursor: "pointer" }}>
                          {STATUSES.map(s => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-4">
                        <div className="d-flex gap-2 align-items-center">
                          {a.status === "Pending" && (
                            <button className="btn btn-sm fw-bold text-white d-flex align-items-center gap-1" style={{ background: "#D85A30", fontSize: 11, borderRadius: 8 }} onClick={() => handleCheckin(a.schedule_id)}>✓ Check In</button>
                          )}
                          
                          {/* WhatsApp Reminder Button */}
                          {a.status === "Pending" && (
                              <button className="btn btn-sm fw-bold text-white d-flex align-items-center gap-1" style={{ background: "#25D366", fontSize: 11, borderRadius: 8 }} onClick={() => handleWhatsApp(a)} title="Send WhatsApp Reminder">📱 WA</button>
                          )}

                          <button className="btn btn-sm fw-bold" style={{ background: "#E6F1FB", color: "#042C53", fontSize: 11 }} onClick={() => openEdit(a)}>Edit</button>
                          <button className="btn btn-sm fw-bold" style={{ background: "#FCEBEB", color: "#501313", fontSize: 11 }} onClick={() => setDeleteId(a.schedule_id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit/Block Modal */}
      <BSModal open={modalOpen} title={form.schedule_id ? "Edit Item" : (form.status === "Blocked" ? "Block Time Slot" : "Add Appointment")} size="modal-lg" onClose={() => setModalOpen(false)}>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>{form.status === 'Blocked' ? 'TITLE' : 'PATIENT NAME *'}</label>
            <input className={inp} value={form.patient_name} onChange={e => setForm({ ...form, patient_name: e.target.value })} disabled={form.status === 'Blocked' && !!form.schedule_id} placeholder={form.status === 'Blocked' ? 'Reason for block' : 'e.g. Priya Sharma'} />
          </div>
          <div className="col-md-6">
            <label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>DOCTOR *</label>
            <select className="form-select" value={form.doctor_id} onChange={e => setForm({ ...form, doctor_id: e.target.value })}>
              <option value="">Select Doctor</option>
              {doctors.map(d => <option key={d.doctor_id} value={d.doctor_id}>{d.name}</option>)}
            </select>
          </div>
          <div className="col-md-6"><label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>DATE *</label><input type="date" className={inp} value={form.appointment_date} onChange={e => setForm({ ...form, appointment_date: e.target.value })} /></div>
          <div className="col-md-6"><label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>TIME *</label><input type="time" className={inp} value={form.appointment_time} onChange={e => setForm({ ...form, appointment_time: e.target.value })} /></div>
          
          {form.status !== 'Blocked' && (
              <div className="col-md-6"><label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>TREATMENT</label><input className={inp} value={form.treatment} onChange={e => setForm({ ...form, treatment: e.target.value })} placeholder="e.g. Root Canal" /></div>
          )}

          <div className="col-md-6"><label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>STATUS</label><select className="form-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} disabled={form.status === 'Blocked'}>{STATUSES.map(s => <option key={s}>{s}</option>)}</select></div>
          <div className="col-12"><label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>NOTES</label><textarea className={inp} rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes..." /></div>
        </div>
        {error && <div className="alert alert-danger py-2 px-3 mt-3 mb-0" style={{ fontSize: 12 }}>{error}</div>}
        <div className="d-flex gap-2 justify-content-end mt-3">
          <button className="btn btn-light fw-semibold" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="btn fw-bold text-white px-4" style={{ background: "#D85A30" }} onClick={handleSubmit} disabled={submitting}>
            {submitting ? <><span className="spinner-border spinner-border-sm me-1" />Saving...</> : "Save"}
          </button>
        </div>
      </BSModal>

      {/* Patient details quick modal */}
      <BSModal open={!!patientModal} title="Patient Overview" onClose={() => setPatientModal(null)}>
        {patientModal && (
          <div className="px-2 pb-2">
            <h5 className="mb-1 fw-bold" style={{ color: "#0A1628" }}>{patientModal.patient_name}</h5>
            <p className="text-muted small mb-3">Mobile: {patientModal.mobile || "N/A"}</p>
            
            <div className="alert alert-warning py-2 mb-3" style={{ fontSize: 13, borderLeft: "4px solid #ffbc05" }}>
              <strong className="d-block mb-1">Medical Medical / Allergies:</strong>
              {patientModal.allergies || "No known allergies documented."}
            </div>
            
            <div className="mb-2"><strong>Doctor:</strong> {patientModal.doctor_name}</div>
            <div className="mb-2"><strong>Appointment Time:</strong> {patientModal.appointment_time}</div>
            <div><strong>Treatment Notes:</strong> {patientModal.treatment || "None provided"}</div>
            
            <div className="mt-4 pt-3 border-top text-end">
              {patientModal.status === "Completed" && (
                 <button className="btn btn-primary btn-sm fw-bold me-2" onClick={() => { navigate(`/assistant/billing?patient_id=${patientModal.patient_id}`); }}>Jump to Billing</button>
              )}
              <button className="btn btn-light btn-sm fw-bold" onClick={() => setPatientModal(null)}>Close</button>
            </div>
          </div>
        )}
      </BSModal>

      <DeleteModal open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} message="Are you sure you want to remove this appointment?" />
    </div>
  );
}